// src/Dashboard/pages/user/PrepaPhysique/hooks/useUserPrepaPhysique.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../../services/adherentServices";
import {
  getExercisesByAdherent,
  updateAdherentExerciseResult,
} from "../../../../../services/prepaPhysiqueServices";

import {
  DEFAULT_FILTERS,
  getExerciseAssignment,
  getExerciseDone,
  getExerciseDate,
  getExerciseProgramme,
  getExerciseRepetitions,
  isValidDateValue,
  todayISO,
} from "../utils/prepaPhysiqueUserUtils";

export function useUserPrepaPhysique() {
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");

  const [exercisesByAdherent, setExercisesByAdherent] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [imagePreview, setImagePreview] = useState(null);

  const selectedAdherent = useMemo(() => {
    return (
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId)
      ) || null
    );
  }, [adherents, selectedAdherentId]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const list = await getAdherentsByUser();
      const safeAdherents = Array.isArray(list) ? list : [];

      setAdherents(safeAdherents);

      const entries = await Promise.all(
        safeAdherents.map(async (adherent) => {
          try {
            const response = await getExercisesByAdherent(adherent.id);

            const exercises =
              response?.Exercices ||
              response?.exercices ||
              response?.exercises ||
              [];

            return [String(adherent.id), Array.isArray(exercises) ? exercises : []];
          } catch (error) {
            console.error(
              `Erreur chargement exercices adhérent ${adherent.id} :`,
              error
            );

            return [String(adherent.id), []];
          }
        })
      );

      const nextMap = Object.fromEntries(entries);
      setExercisesByAdherent(nextMap);

      setSelectedAdherentId((previous) => {
        if (previous) return previous;
        return safeAdherents?.[0]?.id ? String(safeAdherents[0].id) : "";
      });
    } catch (error) {
      console.error("Erreur récupération préparation physique :", error);
      toast.error("Erreur lors de la récupération des exercices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const exercises = useMemo(() => {
    if (!selectedAdherentId) return [];
    return exercisesByAdherent[String(selectedAdherentId)] || [];
  }, [exercisesByAdherent, selectedAdherentId]);

  const programmes = useMemo(() => {
    const values = new Set();

    exercises.forEach((exercise) => {
      const programme = getExerciseProgramme(exercise);
      if (programme) values.add(programme);
    });

    return Array.from(values).sort((a, b) =>
      String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
    );
  }, [exercises]);

  const filteredExercises = useMemo(() => {
    const search = String(filters.search || "").trim().toLowerCase();

    return exercises.filter((exercise) => {
      const assignment = getExerciseAssignment(exercise);
      const done = getExerciseDone(exercise);
      const programme = getExerciseProgramme(exercise);

      const matchesSearch =
        !search ||
        [
          exercise?.nom,
          exercise?.programme,
          programme,
          exercise?.description,
          assignment?.repetitions,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(search);

      const matchesProgramme =
        !filters.programme || programme === filters.programme;

      const matchesStatus =
        filters.status === "all" ||
        (filters.status === "done" && done) ||
        (filters.status === "todo" && !done);

      return matchesSearch && matchesProgramme && matchesStatus;
    });
  }, [exercises, filters]);

  const stats = useMemo(() => {
    const total = exercises.length;
    const done = exercises.filter((exercise) => getExerciseDone(exercise)).length;
    const todo = total - done;
    const progress = total > 0 ? Math.round((done / total) * 100) : 0;

    return {
      total,
      done,
      todo,
      progress,
      adherents: adherents.length,
      programmes: programmes.length,
    };
  }, [exercises, adherents.length, programmes.length]);

  const updateExerciseInState = useCallback((adherentId, exerciseId, patch) => {
    setExercisesByAdherent((previous) => {
      const key = String(adherentId);
      const list = previous[key] || [];

      return {
        ...previous,
        [key]: list.map((exercise) => {
          if (String(exercise.id) !== String(exerciseId)) return exercise;

          return {
            ...exercise,
            AdherentPrepaPhysique: {
              ...(exercise.AdherentPrepaPhysique || {}),
              ...patch,
            },
          };
        }),
      };
    });
  }, []);

  const handleToggleDone = useCallback(
    async (exercise, adherentId = selectedAdherentId) => {
      if (!exercise?.id || !adherentId) return;

      const assignment = getExerciseAssignment(exercise);
      const currentDone = getExerciseDone(exercise);
      const nextDone = !currentDone;

      const dateRealisation = isValidDateValue(assignment?.date_realisation)
        ? assignment.date_realisation
        : todayISO();

      const payload = {
        adherent_id: adherentId,
        prepa_physique_id: exercise.id,
        date_realisation: dateRealisation,
        repetitions: getExerciseRepetitions(exercise) || 0,
        is_done: nextDone,
      };

      const key = `${adherentId}-${exercise.id}`;

      try {
        setSavingKey(key);

        const response = await updateAdherentExerciseResult(payload);

        if (response === null || response === false) {
          throw new Error("Réponse invalide");
        }

        updateExerciseInState(adherentId, exercise.id, {
          is_done: nextDone,
          date_realisation: dateRealisation,
          repetitions: payload.repetitions,
        });

        toast.success(
          nextDone
            ? "Exercice marqué comme réalisé."
            : "Exercice repassé en non réalisé."
        );
      } catch (error) {
        console.error("Erreur mise à jour exercice :", error);
        toast.error("Erreur lors de la mise à jour de l’exercice.");
      } finally {
        setSavingKey("");
      }
    },
    [selectedAdherentId, updateExerciseInState]
  );

  const openImagePreview = useCallback((exercise) => {
    setImagePreview({
      src: exercise?.lien_photo || "/img/no-image.jpg",
      title: exercise?.nom || "Illustration",
    });
  }, []);

  const closeImagePreview = useCallback(() => {
    setImagePreview(null);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return {
    loading,
    savingKey,

    adherents,
    selectedAdherentId,
    setSelectedAdherentId,
    selectedAdherent,

    exercises,
    filteredExercises,

    filters,
    setFilters,
    programmes,

    stats,

    imagePreview,
    openImagePreview,
    closeImagePreview,

    handleToggleDone,
    resetFilters,
    refresh: loadData,
  };
}

export default useUserPrepaPhysique;